import cn from 'classnames';
import { FC } from 'react';

import { isRuCluster } from '@/shared/lib';
import { Icon } from '@/shared/ui';

import { cisRuLabelingData, labelingData } from '../../config';
import { getLabelingCountryGroup } from '../../lib';

import styles from './LabelingContent.module.scss';

type AccordionContentProps = {
  country: string;
};
export const LabelingContent: FC<AccordionContentProps> = (props) => {
  const { country } = props;

  const isRu = isRuCluster();
  const labelingCountryGroup = getLabelingCountryGroup(country);

  // On the RU cluster every country shows the same CIS manufacturer info
  const data = isRu
    ? cisRuLabelingData
    : labelingCountryGroup && labelingData[labelingCountryGroup];

  if (!data) {
    return null;
  }

  const isCanada = labelingCountryGroup === 'Canada';

  const {
    manufacturer,
    contactInfo,
    UDI,
    productVersion,
    marking,
    userManual,
    userManualFr,
    lastUpdate,
    nameOfProduct,
  } = data;

  const title = contactInfo?.title;
  const address = contactInfo?.address;
  const email = contactInfo?.email;
  const phoneNumber = contactInfo?.phoneNumber;

  return (
    <div className={styles.container} data-component="about-labeling-content">
      <div className={styles.wrapperWithGap}>
        {nameOfProduct && (
          <div className={styles.marginBottom}>
            <h6 className={cn(styles.inline, 'h6')}>
              {isRu ? 'Наименование продукта' : 'Name of product'}
              {isCanada && ` / Nom du produit`}:
            </h6>{' '}
            <span className="p2">{nameOfProduct}</span>
          </div>
        )}

        <h6 className={cn(styles.marginBottom, 'h6')}>
          {isRu ? 'Производитель' : 'Manufacturer'}
          {isCanada && ` / Fabricant`}
        </h6>
        <p className="p2">{manufacturer}</p>
      </div>

      {contactInfo && (
        <div className={styles.wrapperWithGap}>
          {title && <h6 className={cn(styles.marginBottom, 'h6')}>{title}</h6>}

          {address && <p className="p2">{address}</p>}

          {(phoneNumber || email) && (
            <div className={styles.wrapper}>
              {phoneNumber && (
                <a
                  href={`tel:${phoneNumber}`}
                  className={cn(styles.link, 'p2')}
                >
                  {phoneNumber}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className={cn(styles.link, 'p2')}>
                  {email}
                </a>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.wrapperWithGap}>
        <h6 className={cn(styles.marginBottom, 'h6')}>
          {isRu ? 'Версия' : 'Version'}
        </h6>

        <p className="p2">{productVersion}</p>
      </div>

      {lastUpdate && (
        <div className={styles.wrapperWithGap}>
          <h6 className={cn(styles.marginBottom, 'h6')}>
            {isRu ? 'Дата обновления' : 'Update date'}
          </h6>

          <p className="p2">{lastUpdate}</p>
        </div>
      )}

      {UDI !== null && <h6 className="h6">UDI: {UDI}</h6>}

      {userManual && (
        <div className={styles.wrapperWithGap}>
          <h6 className={cn(styles.marginBottom, 'h6')}>
            {isRu ? 'Руководство пользователя' : 'User manual'}
            {isCanada && ` / Manuel d’utilisation`}
          </h6>

          <div className={styles.linkWrapper}>
            <a
              href={userManual}
              target="_blank"
              className={cn(styles.download, styles.link, 'p2')}
              rel="noreferrer"
            >
              {isRu ? 'Скачать' : 'Download'}
            </a>

            {isCanada && (
              <>
                {' / '}

                <a
                  href={userManualFr ?? userManual}
                  target="_blank"
                  className={cn(styles.download, styles.link, 'p2')}
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </>
            )}
          </div>
        </div>
      )}

      {marking && (
        <div className={styles.marking}>
          {marking.CE && <Icon name="CEMarking" width={32} height={24} />}

          {marking.MD && <Icon name="MDMarking" width={44} height={28} />}

          {marking.rxOnly && (
            <Icon name="rxOnlyMarking" width={60} height={28} />
          )}

          {marking.iso15223 && (
            <Icon name="iso15223Marking" width={24} height={28} />
          )}
        </div>
      )}
    </div>
  );
};
